import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import attendikLogo from "../assets/attendik-logo.png";
import { CountsError, getCounts, type Counts } from "../api/client.js";
import { AttendeesButton } from "../components/AttendeesButton.js";
import { SnapshotButton } from "../components/SnapshotButton.js";

const ACTIVE_INTERVAL_MS = 3000;
const HIDDEN_INTERVAL_MS = 10_000;

// Above this age, the indicator drops from "live" to "reconnecting" even if
// the last fetch happened to succeed — covers the case where the network
// stalls between polls.
const RECONNECTING_THRESHOLD_MS = 10_000;

interface DisplayState {
  counts: Counts | null;
  /** Status code of the most recent fetch failure (401, 404, …). */
  fatal: number | null;
  /** Wall-clock timestamp of the last successful poll. */
  lastUpdate: number | null;
}

/**
 * The live board for one event. Reads `eventId` from the URL and `token` from
 * the query string; polls `GET /m/v1/counts/:eventId?token=…` every 3s while
 * visible and every 10s when the tab is hidden.
 *
 * On 401: stops polling and shows an "Invalid link" view.
 * On 404: stops polling and shows an "Event not found" view.
 * Any other error keeps the last successful board on screen, with the
 * indicator changing to "● reconnecting…". The next successful poll restores
 * the green live indicator.
 */
export function BoardPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [state, setState] = useState<DisplayState>({
    counts: null,
    fatal: null,
    lastUpdate: null
  });
  const [, force] = useState({});

  // Refs so the poll loop reads the current visibility / fatal state without
  // re-binding the interval every render.
  const fatalRef = useRef<number | null>(null);
  const pollRef = useRef<(() => void) | null>(null);

  const poll = useCallback(async (): Promise<void> => {
    if (!eventId || !token) {
      return;
    }
    if (fatalRef.current !== null) {
      return;
    }
    try {
      const counts = await getCounts(eventId, token);
      setState({ counts, fatal: null, lastUpdate: Date.now() });
    } catch (err) {
      if (err instanceof CountsError && (err.status === 401 || err.status === 404)) {
        fatalRef.current = err.status;
        setState((prev) => ({ ...prev, fatal: err.status }));
        return;
      }
      // Transient — keep the last successful counts on screen so the board
      // doesn't flash empty during a brief network blip.
      setState((prev) => ({ ...prev, lastUpdate: prev.lastUpdate }));
    }
  }, [eventId, token]);

  pollRef.current = poll;

  useEffect(() => {
    if (!eventId || !token) {
      return undefined;
    }
    // Immediate fetch on mount so the board comes up populated.
    void poll();

    let interval: ReturnType<typeof setInterval>;
    const arm = (): void => {
      clearInterval(interval);
      const ms =
        document.visibilityState === "hidden"
          ? HIDDEN_INTERVAL_MS
          : ACTIVE_INTERVAL_MS;
      interval = setInterval(() => {
        void (pollRef.current?.() ?? Promise.resolve());
      }, ms);
    };
    arm();

    const onVisibility = (): void => {
      arm();
      // A fresh poll when the user comes back keeps the board from showing
      // stale numbers while waiting for the next tick.
      if (document.visibilityState !== "hidden") {
        void poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Tick the "X seconds ago" label once a second.
    const labelTimer = setInterval(() => force({}), 1000);

    return () => {
      clearInterval(interval);
      clearInterval(labelTimer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [eventId, token, poll]);

  if (!eventId || !token) {
    return <InvalidLinkView />;
  }
  if (state.fatal === 401) {
    return <InvalidLinkView />;
  }
  if (state.fatal === 404) {
    return <NotFoundView />;
  }
  if (!state.counts) {
    return <LoadingView />;
  }

  return (
    <Board
      counts={state.counts}
      lastUpdate={state.lastUpdate}
      eventId={eventId}
      token={token}
    />
  );
}

// ---- Live board -------------------------------------------------------------

function Board({
  counts,
  lastUpdate,
  eventId,
  token
}: {
  counts: Counts;
  lastUpdate: number | null;
  eventId: string;
  token: string;
}): JSX.Element {
  const previous = usePrevious(counts);

  const pulseCurrentlyIn = previous && previous.currently_in !== counts.currently_in;

  return (
    <main className="flex h-full min-h-screen flex-col bg-slate-950 text-white">
      <header className="monitor-no-print flex items-center justify-between gap-3 px-6 py-4 text-sm sm:px-10">
        <div className="flex min-w-0 items-center gap-4">
          <img
            src={attendikLogo}
            alt="Attendik"
            className="h-7 w-auto shrink-0 [filter:invert(1)]"
          />
          <h1 className="truncate font-medium text-slate-200">{counts.event_name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <AttendeesButton eventId={eventId} token={token} />
          <SnapshotButton eventId={eventId} token={token} />
          <LiveIndicator lastUpdate={lastUpdate} />
        </div>
      </header>

      <section className="flex flex-1 flex-col items-center justify-center px-4 text-center">
        <div className="monitor-headline">
          {/* clamp(3rem, 22vw, 18rem) — scales from a phone (~48px) up to a
              foyer-screen number well over 150pt without ever overflowing. */}
          <div className="flex items-end justify-center font-sans font-black leading-none tracking-tight">
            <span
              data-testid="currently-in"
              className={
                "block " +
                "text-[clamp(3rem,22vw,18rem)] " +
                (pulseCurrentlyIn ? "animate-pulse-pop" : "")
              }
            >
              {counts.currently_in}
            </span>
            <span className="ml-3 pb-[0.18em] text-[clamp(1.5rem,7vw,5rem)] font-bold text-slate-500">
              / {counts.registered}
            </span>
          </div>
          <p className="mt-2 text-base uppercase tracking-[0.3em] text-slate-400 sm:text-lg">
            currently in room
          </p>
        </div>
      </section>
    </main>
  );
}

function LiveIndicator({ lastUpdate }: { lastUpdate: number | null }): JSX.Element {
  const age = lastUpdate ? Date.now() - lastUpdate : null;
  const reconnecting = age === null || age > RECONNECTING_THRESHOLD_MS;
  if (reconnecting) {
    return (
      <span
        data-testid="indicator"
        data-status="reconnecting"
        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-amber-300"
      >
        <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden />
        reconnecting…
      </span>
    );
  }
  const seconds = age === null ? 0 : Math.max(0, Math.floor(age / 1000));
  return (
    <span
      data-testid="indicator"
      data-status="live"
      className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-emerald-300"
    >
      <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden />
      live · {seconds}s ago
    </span>
  );
}

// ---- States ----------------------------------------------------------------

function LoadingView(): JSX.Element {
  return (
    <main className="flex h-full min-h-screen items-center justify-center bg-slate-950 text-slate-400">
      <p className="text-sm uppercase tracking-[0.3em]">Loading…</p>
    </main>
  );
}

function InvalidLinkView(): JSX.Element {
  return (
    <main className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
      <h2 className="text-2xl font-semibold">Invalid link</h2>
      <p className="max-w-md text-sm text-slate-400">
        This monitor link is no longer valid. Ask the event organiser to share
        an up-to-date link.
      </p>
    </main>
  );
}

function NotFoundView(): JSX.Element {
  return (
    <main className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
      <h2 className="text-2xl font-semibold">Event not found</h2>
      <p className="max-w-md text-sm text-slate-400">
        The event for this link does not exist (or has been removed).
      </p>
    </main>
  );
}

// ---- helpers ---------------------------------------------------------------

function usePrevious<T>(value: T): T | null {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
